import { Form } from 'antd';
import { useField } from 'formik';
import { isEmpty, isEqual, isNil } from 'lodash/fp';
import numeral from 'numeral';
import { useCallback, useState, useMemo } from 'react';
import { type InputFieldProps } from '../../../components/fields/InputNumberField';
import { StyledTextFieldWrapper } from './shared';
import useRenderTooltip from './useRenderTooltip';
import { getTextFieldState } from './utils';

const InputNumberField = ({
    placeholder,
    status,
    label,
    required = false,
    name,
    itemProps,
    tooltip,
    formatter,
    disabled,
    addonAfter,
    addonBefore,
}: InputFieldProps) => {
    const [userTyping, setIsTyping] = useState(false);

    const onFocus = useCallback<React.FocusEventHandler<HTMLInputElement>>(ev => {
        setIsTyping(true);
    }, []);

    const onBlur = useCallback<React.FocusEventHandler<HTMLInputElement>>(ev => {
        setIsTyping(false);
    }, []);

    const [field, meta, { setValue }] = useField({ name, onFocus, onBlur });

    const onNumberChange: React.ChangeEventHandler<HTMLInputElement> = e => {
        setValue(numeral(e.target.value).value());
    };

    const renderTooltip = useRenderTooltip(tooltip);

    const value = useMemo(() => {
        if (isNil(field.value)) {
            return '';
        }

        return formatter
            ? formatter(numeral(field.value).value(), { input: field.value?.toString(), userTyping })
            : field.value;
    }, [field.value, formatter, userTyping]);

    const hasError = !!meta?.error && (meta?.touched || !isEqual(meta?.initialValue, meta?.value));
    const errorMessage = Array.isArray(meta?.error) ? '' : meta?.error;

    return (
        <Form.Item {...itemProps} required={required}>
            <StyledTextFieldWrapper
                hideLabel={isEmpty(label)}
                state={hasError ? 'error' : getTextFieldState(status)}
                // Only support 1 since, porsche v3 only had unit for prefix or suffix, not both.
                // And the type is string
                unit={(addonBefore ?? addonAfter) as unknown as string}
                unitPosition={addonBefore ? 'prefix' : 'suffix'}
            >
                <span slot="label">
                    {label}
                    {renderTooltip()}
                </span>
                {itemProps?.help && <span slot="description">{itemProps?.help}</span>}
                <input
                    {...field}
                    disabled={disabled}
                    onChange={onNumberChange}
                    placeholder={placeholder}
                    required={required}
                    type="text"
                    value={value}
                />
                {hasError && <span slot="message">{errorMessage}</span>}
            </StyledTextFieldWrapper>
        </Form.Item>
    );
};

export default InputNumberField;
